import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ListChecks, PenLine, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { displayName } from "@/lib/patientFormat";
import { Skeletons, DqMarker, useProviders } from "./psPrimitives";
import { usePsClient } from "./psMasters";
import { ConfirmSheet } from "./ProvidersScreen";
import { ProviderList, useProviderConfigs } from "./AvailabilityScreen";
import { IN_CSS, IBASE, AREAS, designSub, intakeMeta, sig, qList, cleanQs, slowErr } from "./intakeShared";
import { QuestionsTile, DraftBar } from "./IntakeQuestions";
import { DirectionTile } from "./IntakeDirection";
import { PreviewTile } from "./IntakePreview";

const rewrittenMemo = new Map();
const GAPS = [["Patient reminders", "When the intake link is sent, and re-sent if unanswered."], ["Waiting-room message", "What patients read while they wait for the provider."]];

function Tiles({ tab, setTab, tiles, full }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3" data-testid="in-tiles">
      {tiles.map(({ id, icon: Icon, label, sub, gap }) => { const on = tab === id; return (
        <button key={id} type="button" onClick={() => setTab(id)} data-testid={`in-tile-${id}`}
          className={`text-left rounded-[14px] border p-3.5 bg-white transition-colors ${on ? "border-[#5856D6]" : "border-[#E9E9EC] hover:bg-black/[.02]"}`} style={on ? { boxShadow: "0 0 0 3px rgba(88,86,214,.14)" } : undefined}>
          <div className="flex items-center justify-between gap-2"><Icon size={16} className={on ? "text-[#5856D6]" : "text-[var(--gray2)]"} />{full && gap ? <DqMarker n={`in-${id}`} /> : null}</div>
          <div className="text-[12.5px] font-semibold text-[var(--ink)] mt-2">{label}</div>
          <div className="text-[11px] text-[var(--gray2)] mt-0.5 leading-snug" data-testid={`in-tile-${id}-sub`}>{sub}</div>
        </button>); })}
    </div>
  );
}

function ProviderIntake({ cid, p, onBack, mode }) {
  const qc = useQueryClient();
  const npi = String(p.npi_number), key = `${cid}-${npi}`;
  const { data: cfgs = [], isLoading } = useProviderConfigs(cid);
  const cfg = cfgs.find((c) => String(c.npi) === npi) || {};
  const design = cfg.intake_design || null, savedQs = design?.questions || [];
  const [tab, setTab] = useState("questions");
  const [draft, setDraft] = useState(null);
  const [depth, setDepth] = useState("mini"), [hist, setHist] = useState(true), [areas, setAreas] = useState(AREAS.map(([k]) => k));
  const [gen, setGen] = useState(false), [genErr, setGenErr] = useState(false), [ask, setAsk] = useState(false);
  const [saving, setSaving] = useState(false), [saveErr, setSaveErr] = useState(false);
  const [rewritten, setRewritten] = useState(rewrittenMemo.get(key));
  const [init, setInit] = useState(false), [savedNote, setSavedNote] = useState(null);
  const [docBusy, setDocBusy] = useState(false), [docErr, setDocErr] = useState(null), [docSrc, setDocSrc] = useState(null), [docAsk, setDocAsk] = useState(null);
  useEffect(() => {
    if (isLoading || init) return;
    if (design) { setDepth(design.depth || "mini"); setHist(design.include_new_patient_history ?? true); if (design.history_areas?.length) setAreas(design.history_areas); }
    setInit(true);
  }, [isLoading, init, design]);
  if (isLoading || !init) return <Skeletons />;
  const list = draft ?? savedQs, dirty = draft != null && sig(draft) !== sig(savedQs);
  const refresh = () => qc.invalidateQueries({ queryKey: ["provider-configs", cid] });
  const generate = async () => {
    setAsk(false); setGen(true); setGenErr(false);
    try {
      const { data } = await api.post(`${IBASE}/intake-design/generate`, { client_id: String(cid), npi, depth, include_new_patient_history: hist, ...(hist ? { history_areas: areas } : {}) }, { timeout: 180000 });
      const qs = qList(data);
      if (!qs.length) { setGenErr("empty"); return; }
      if (sig(cleanQs(qs)) === sig(cleanQs(list))) { setGenErr("same"); return; }
      setDraft(qs); setSavedNote(null); setDocSrc(null);
    } catch (e) { setGenErr(slowErr(e) ? "slow" : true); } finally { setGen(false); }
  };
  // Release 17 Oct C.3: build the questions from the provider's own form (not saved until Save).
  const pickDoc = (file) => {
    setDocErr(null);
    if (file.size > 8 * 1024 * 1024) { setDocErr("big"); return; }
    if (!/\.(pdf|docx|txt)$/i.test(file.name)) { setDocErr("type"); return; }
    if ((draft ?? savedQs).length) { setDocAsk(file); return; }
    fromDoc(file);
  };
  const fromDoc = async (file) => {
    setDocAsk(null); setDocBusy(true); setDocErr(null); setGenErr(false);
    try {
      const fd = new FormData();
      fd.append("client_id", String(cid)); fd.append("npi", npi); fd.append("depth", depth); fd.append("include_new_patient_history", String(hist)); fd.append("file", file);
      const { data } = await api.post(`${IBASE}/intake-design/from-document`, fd, { timeout: 180000 });
      const qs = qList(data);
      if (!qs.length) { setDocErr("none"); return; }
      setDraft(qs); setDocSrc(data?.source_filename || file.name); setSavedNote(null);
    } catch (e) {
      const st = e?.response?.status, det = e?.response?.data?.detail;
      const msg = typeof det === "string" ? det : "";
      setDocErr(st === 422 ? "none" : st === 413 || /large|8 ?mb/i.test(msg) ? "big" : st === 400 ? (/empty|no text|scan|image|unreadable|text pdf/i.test(msg) || !msg ? "scan" : msg) : (msg || "Couldn't read the form. Your questions weren't changed."));
    } finally { setDocBusy(false); }
  };
  const saveDesign = async () => {
    setSaving(true); setSaveErr(false);
    try {
      const sent = cleanQs(draft);
      const { data } = await api.put(`${IBASE}/intake-design`, { client_id: String(cid), npi, depth, include_new_patient_history: hist, ...(hist ? { history_areas: areas } : {}), questions: sent });
      const kept = (data?.intake_design?.questions || sent).length;
      const fresh = ((await api.get(`${IBASE}?client_id=${encodeURIComponent(cid)}&npi=${npi}`)).data?.configs || [])[0];
      const g = data?.intake_guidance ?? data?.config?.intake_guidance ?? fresh?.intake_guidance ?? "";
      rewrittenMemo.set(key, g); setRewritten(g); setSavedNote({ guidance: g, kept, sent: sent.length, cut: kept < sent.length });
      await refresh(); setDraft(null); setDocSrc(null);
    } catch { setSaveErr(true); } finally { setSaving(false); }
  };
  const saveDirection = async (text) => { await api.put(`${IBASE}/intake-guidance`, { client_id: String(cid), npi, intake_guidance: text }); await refresh(); };
  const dirN = (cfg.intake_guidance || "").length;
  const tiles = [
    { id: "questions", icon: ListChecks, label: "Questions", sub: designSub(design) || "Not designed yet", gap: true },
    { id: "direction", icon: PenLine, label: "Direction", sub: dirN ? `${dirN.toLocaleString()} / 1,500 characters` : "None" },
    { id: "preview", icon: Eye, label: "Preview", sub: "See the form a patient gets", gap: true },
  ];
  return (
    <div className="fade-up" data-testid="in-provider-page">
      <style>{IN_CSS}</style>
      <button onClick={onBack} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--gray2)] hover:text-[var(--ink)] mb-3" data-testid="in-back"><ChevronLeft size={14} /> Pre-visit intake</button>
      <h2 className="text-[22px] font-bold tracking-[-.01em] text-[var(--ink)]">{displayName(p.provider_name)}</h2>
      <div className="text-[13px] text-[var(--gray2)] mt-1.5 mb-[18px]">NPI {npi}</div>
      <Tiles tab={tab} setTab={setTab} tiles={tiles} full={mode === "full"} />
      {tab === "questions" ? <QuestionsTile list={list} setList={setDraft} onGenerate={() => (dirty ? setAsk(true) : generate())}
        taxonomy={((p.taxonomies || []).find((t) => t.primary) || (p.taxonomies || [])[0])?.desc}
        s={{ depth, setDepth, hist, setHist, areas, setAreas, gen, genErr, retry: () => (dirty ? setAsk(true) : generate()), savedNote, openDirection: () => setTab("direction"), fromDoc: pickDoc, docBusy, docErr, docSrc: draft != null ? docSrc : null }} /> : null}
      {tab === "direction" ? <DirectionTile saved={cfg.intake_guidance || ""} rewritten={rewritten} onSave={saveDirection} /> : null}
      {tab === "preview" ? <PreviewTile cid={cid} provider={p} draft={draft ? cleanQs(draft) : null} dirty={dirty} hasAny={!!savedQs.length || draft != null}
        onGoQuestions={() => setTab("questions")} onUseVersion={(qs) => { setDraft(qs); setTab("questions"); }} /> : null}
      {dirty && tab === "questions" ? <DraftBar busy={saving} err={saveErr} onDiscard={() => { setDraft(null); setDocSrc(null); }} onSave={saveDesign} /> : null}
      {docAsk ? <ConfirmSheet title="Replace your questions with the ones from your form?" body={`The questions from ${docAsk.name} become an unsaved draft. Nothing changes for patients until you press Save.`} confirmLabel="Replace" onConfirm={() => fromDoc(docAsk)} onCancel={() => setDocAsk(null)} /> : null}
      {ask ? <ConfirmSheet title="Replace your draft?" body="Your edits to these questions will be lost." confirmLabel="Replace" onConfirm={generate} onCancel={() => setAsk(false)} /> : null}
    </div>
  );
}

export function IntakeScreen({ mode, onGoProviders }) {
  const { cid } = usePsClient();
  const { data: providers = [] } = useProviders();
  const [npi, setNpi] = useState(null);
  const p = npi ? providers.find((x) => String(x.npi_number) === String(npi)) : null;
  if (p && cid) return <ProviderIntake key={`${cid}-${npi}`} cid={cid} p={p} mode={mode} onBack={() => setNpi(null)} />;
  return <ProviderList testid="in" eyebrow="Scheduling (telehealth) · Step 2 of 2" title="Pre-visit intake" mode={mode} gaps={GAPS} onGoProviders={onGoProviders}
    lede="The questions a patient answers before a telehealth visit. Lumina writes them for each provider's specialty; patients mostly tap."
    metaOf={intakeMeta} onPick={(x) => setNpi(x.npi_number)} />;
}
